import { createFileRoute } from '@tanstack/react-router';
import * as v from 'valibot';
import { useTranslation } from '@/lib/i18n';
import { useDeferredValue, useState, useEffect, useRef, Suspense, startTransition } from 'react';
import { isServer, useSuspenseQuery } from '@tanstack/react-query';
import { type LiveCounterMode } from '@/lib/data';
import { WINDOW_REFETCH_MS } from '@/lib/live-window';
import { trpc } from '@/lib/trpc';
import { toast } from '@/lib/toast';
import { AnimatedTotalRaised } from '@/routes/-components/animated-total-raised';
import type { Locale } from '@worldcoo/monolith/core';
import { flagUrls, countryCodeToLocale } from '@/lib/country-flags';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { motion, AnimatePresence } from 'motion/react';
import { APP_VERSION_STORAGE_KEY } from '@/lib/app-version';

export const Route = createFileRoute('/www/live-counter')({
	validateSearch: v.object({
		// `day` is the default on purpose. `realtime` replays the last six minutes, and BI ingests
		// donations with more lag than that, so the realtime window is usually empty — the counters
		// sit on a frozen number and the screen looks broken. A 24h offset always has data to replay.
		mode: v.fallback(v.picklist(['day', 'realtime']), 'day'),
	}),
	head: () => ({
		meta: [{ name: 'robots', content: 'noindex, nofollow' }],
	}),
	component: Component,
	pendingComponent: LiveCounterPending,
});

// This page has none of the site chrome (no header, no footer, it is a full screen counter meant to
// be left open on a screen), so it gets its own pending UI instead of the site's, and reuses the
// same "..." placeholder its counters already fall back to. A plain <main> rather than the group's
// `Layout` for the same reason: that one applies the redesign's `font-brand`/`text-ink` baseline and
// a `min-h-screen` that would fight the `h-dvh` below.
// `h-dvh overflow-hidden`, not `min-h-dvh`: the donation feed is absolutely positioned below the
// counter so it can never shift it, which also means it can never be bounded by it. With an emergency
// active the global counter starts halfway down the screen and that feed ran ~950px past the fold —
// on a wall display, half the feed off-screen behind a scrollbar. Nothing on this page scrolls, so
// pinning the page to exactly one viewport clips the overflow instead of growing the document.
const PAGE_CLASS = 'flex h-dvh flex-col items-center justify-start overflow-hidden';
const COUNTERS_STACK_CLASS =
	'flex w-full flex-col items-center gap-[clamp(1.25rem,3vh,2.5rem)] px-6 pt-[clamp(1.25rem,3.5vh,3rem)]';

function LiveCounterPending() {
	return (
		<main className={PAGE_CLASS}>
			<div className={COUNTERS_STACK_CLASS}>
				<CounterLogo />
				<CounterFallback />
			</div>
		</main>
	);
}

/**
 * The page masthead. It sits above BOTH counters rather than inside the global one: with an
 * emergency active the global counter is the second block on the page, which used to leave the logo
 * stranded in the middle of the screen, under the emergency card.
 */
function CounterLogo({ compact = false }: { compact?: boolean }) {
	return (
		<WorldcooLogo
			className={`${compact ? 'h-[clamp(2.25rem,4.5vw,5rem)]' : 'h-[clamp(3rem,6vw,8rem)]'} w-auto`}
			accentClassName="text-primary"
		/>
	);
}

// ---------------------------------------------------------------------------
// Country flags (SVG icon components)
// ---------------------------------------------------------------------------

function getCountryFlagUrl(countryCode: string) {
	const locale = countryCodeToLocale[countryCode];
	return locale ? flagUrls[locale] : undefined;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ApiDonation = {
	amountCents: number;
	timestamp: number;
	campaignName: string | null;
	merchantName: string | null;
	city: string | null;
	country: string | null;
};

type FeedDonation = ApiDonation & { id: string };

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function Component() {
	const { mode } = Route.useSearch();
	const navigate = Route.useNavigate();

	useReloadOnNewVersion();

	const handleToggle = () => {
		startTransition(() => {
			void navigate({
				search: { mode: mode === 'day' ? 'realtime' : 'day' },
				replace: true,
			});
		});
	};

	return (
		<main className={PAGE_CLASS}>
			<div className={COUNTERS_STACK_CLASS}>
				<Suspense fallback={<CounterFallback />}>
					<Counters mode={mode} />
				</Suspense>
			</div>

			<ModeToggle mode={mode} onToggle={handleToggle} />
			<MysteriousFruit />
		</main>
	);
}

function CounterFallback() {
	return (
		<div className="text-primary text-center text-[clamp(3.5rem,11vw,12rem)] leading-none font-bold tracking-tight">
			<span className="opacity-40">...</span>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Reload on a new deploy
// ---------------------------------------------------------------------------

const VERSION_CHECK_MS = 15_000;
const RELOAD_GRACE_MS = 8_000;

function readStoredVersion(): string | null {
	if (isServer) return null;
	try {
		return window.localStorage.getItem(APP_VERSION_STORAGE_KEY);
	} catch {
		// Storage unavailable — behave as if the version were unknown and never reload.
		return null;
	}
}

/**
 * Reload the screen when the deploy behind it changes.
 *
 * This is the one page in the app that is meant to be left open for weeks on a wall display, so it
 * is the one page that will still be running last month's bundle. Nothing is fetched here to find
 * that out: every response the start instance serves carries `x-worldcoo-app-version`, and both
 * browser transports write it to localStorage via `syncVersionFromHeader`. On this page the tRPC
 * poll is the only transport in play — it is what makes the value move, and it is why that sync had
 * to be added to the tRPC link and not just to the server-function fetch.
 *
 * The baseline is ADOPTED rather than assumed: on a first visit localStorage is empty until the
 * first response lands, and reading that first write as "the version changed" would reload the page
 * seconds after it opened, forever.
 */
function useReloadOnNewVersion() {
	const baselineVersion = useRef(readStoredVersion());

	useEffect(() => {
		let reloadTimeout: ReturnType<typeof setTimeout> | undefined;

		const timer = setInterval(() => {
			if (reloadTimeout) return;

			const latestVersion = readStoredVersion();
			if (!latestVersion) return;

			if (baselineVersion.current === null) {
				baselineVersion.current = latestVersion;
				return;
			}
			if (latestVersion === baselineVersion.current) return;

			toast.info(`New version available: ${latestVersion.substring(0, 6)}`, {
				description: 'Reloading…',
				duration: RELOAD_GRACE_MS,
			});
			reloadTimeout = setTimeout(() => window.location.reload(), RELOAD_GRACE_MS);
		}, VERSION_CHECK_MS);

		return () => {
			clearInterval(timer);
			if (reloadTimeout) clearTimeout(reloadTimeout);
		};
	}, []);
}

// ---------------------------------------------------------------------------
// Live counter with donation feed
// Same mechanism as the landing page (AnimatedTotalRaised + real timestamps)
// ---------------------------------------------------------------------------

/**
 * Tracks which donations have "arrived" (their shifted timestamp has been
 * reached) and exposes them as a capped, growing feed. Shared by the global
 * and emergency counters. The `feedKey` keeps the two feeds' ids disjoint, and
 * resetting it (or changing mode) clears the feed.
 */
function useDonationFeed(donations: ApiDonation[], mode: LiveCounterMode, feedKey: string): FeedDonation[] {
	const arrivedIdsRef = useRef(new Set<string>());
	const [feedItems, setFeedItems] = useState<FeedDonation[]>([]);

	// Reset feed when the mode or feed identity changes
	const prevKeyRef = useRef(`${feedKey}:${mode}`);
	useEffect(() => {
		const key = `${feedKey}:${mode}`;
		if (prevKeyRef.current !== key) {
			prevKeyRef.current = key;
			arrivedIdsRef.current = new Set();
			setFeedItems([]);
		}
	}, [feedKey, mode]);

	// Poll for newly arrived donations at their real timestamps.
	// IDs are content-based (feedKey+mode+timestamp+amount) so that:
	// 1. placeholderData from a different mode doesn't block real data (different timestamps)
	// 2. Donations keep stable IDs across refetches even if array indices shift
	// We never clear the feed — old items stay and new-window donations append
	// naturally, just like the counter transitions seamlessly.
	useEffect(() => {
		const tick = () => {
			const now = Date.now();
			let hasNew = false;
			const newArrivals: FeedDonation[] = [];
			for (const d of donations) {
				const id = `${feedKey}-${mode}-${d.timestamp}-${d.amountCents}`;
				if (d.timestamp <= now && !arrivedIdsRef.current.has(id)) {
					arrivedIdsRef.current.add(id);
					newArrivals.push({ ...d, id });
					hasNew = true;
				}
			}
			if (hasNew) {
				setFeedItems((prev) => [...prev, ...newArrivals].slice(-20));
			}
		};

		tick();
		const timer = setInterval(tick, 50);
		return () => clearInterval(timer);
	}, [donations, mode, feedKey]);

	return feedItems;
}

// ---------------------------------------------------------------------------
// Counters — fetches both the global and emergency totals together so it can
// lay them out as a single unit. When an emergency is active, both counters
// shrink so the two fit on screen at once; the emergency one sits on top.
// ---------------------------------------------------------------------------

function Counters({ mode }: { mode: LiveCounterMode }) {
	const { language } = useTranslation();
	// Stable keys: the server derives the window, so a rollover refetches these two entries rather than
	// creating a pair of new ones. This screen is left running for weeks, which is what made it the worst
	// contributor to the cache growth behind TEC-3807. `lib/live-window.ts` has the reasoning.
	const liveOptions = {
		staleTime: 60 * 1000,
		gcTime: 15 * 60 * 1000,
		refetchInterval: WINDOW_REFETCH_MS,
		refetchIntervalInBackground: true,
	};
	const { data: globalData } = useSuspenseQuery(trpc.portal.getRecentDonations.queryOptions({ mode }, liveOptions));
	const { data: emergencyData } = useSuspenseQuery(
		trpc.portal.getEmergencyDonations.queryOptions({ mode }, liveOptions),
	);
	const hasEmergency = emergencyData != null;

	return (
		<>
			<CounterLogo compact={hasEmergency} />
			{hasEmergency && <EmergencyCounter data={emergencyData} mode={mode} language={language} />}
			<LiveCounter data={globalData} mode={mode} language={language} compact={hasEmergency} />
		</>
	);
}

function LiveCounter({
	data,
	mode,
	language,
	compact,
}: {
	data: { totalAtWindowStartCents: number; donations: ApiDonation[] };
	mode: LiveCounterMode;
	language: Locale;
	/** Shrink the counter so it shares the screen with the emergency counter. */
	compact: boolean;
}) {
	const deferredData = useDeferredValue(data);
	const feedItems = useDonationFeed(deferredData.donations, mode, 'global');

	return (
		<div className="relative w-full">
			{/* Tagline — the logo is the page masthead now, one level up in <Counters> */}
			<p className="mb-6 text-center text-[clamp(1rem,1.8vw,2.1rem)] tracking-wide">Donations collected</p>

			{/* Counter — centered, stable position */}
			<div
				className={`text-primary text-center leading-none font-bold tracking-tight ${
					compact ? 'text-[clamp(2.5rem,7vw,6.5rem)]' : 'text-[clamp(4rem,13vw,14rem)]'
				}`}
			>
				<AnimatedTotalRaised
					totalAtWindowStartCents={deferredData.totalAtWindowStartCents}
					donations={deferredData.donations}
					language={language}
				/>
			</div>

			{/* Feed — absolutely positioned below the counter so it never shifts it */}
			<div className="absolute inset-x-0 top-full flex justify-center px-8">
				<DonationFeed donations={feedItems} variant={compact ? 'global-compact' : 'global'} />
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Emergency counter — sits above the global counter, styled in red. Only
// rendered by <Counters> when the server reports an active emergency. Its feed
// is kept compact and in normal flow so it never overlaps the global counter.
// ---------------------------------------------------------------------------

function EmergencyCounter({
	data,
	mode,
	language,
}: {
	data: { totalAtWindowStartCents: number; campaignCount: number; donations: ApiDonation[] };
	mode: LiveCounterMode;
	language: Locale;
}) {
	const deferredData = useDeferredValue(data);
	const feedItems = useDonationFeed(deferredData.donations, mode, 'emergency');

	return (
		<div className="flex w-full max-w-5xl flex-col items-center rounded-3xl border-2 border-red-200 bg-red-50/70 px-6 py-[clamp(1rem,2.5vh,2rem)]">
			{/* Header — pulsing badge */}
			<div className="mb-3 flex items-center gap-2.5 rounded-full bg-red-600 px-4 py-1.5 text-white shadow-sm">
				<span className="relative flex h-2 w-2">
					<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
					<span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
				</span>
				<span className="text-[clamp(0.75rem,1.2vw,1.3rem)] font-semibold tracking-wide uppercase">
					Active emergency
				</span>
			</div>

			<p className="mb-1 text-[clamp(0.9rem,1.5vw,1.8rem)] tracking-wide text-red-700">
				Emergency donations collected
				{data.campaignCount > 0 && ` across ${data.campaignCount} campaign${data.campaignCount === 1 ? '' : 's'}`}
			</p>

			{/* Counter — red */}
			<div className="text-center text-[clamp(2.25rem,6.5vw,6rem)] leading-none font-bold tracking-tight text-red-600">
				<AnimatedTotalRaised
					totalAtWindowStartCents={deferredData.totalAtWindowStartCents}
					donations={deferredData.donations}
					language={language}
				/>
			</div>

			{/* Compact feed in normal flow so it never overlaps the global counter */}
			<DonationFeed donations={feedItems} variant="emergency" />
		</div>
	);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** "EROSKI68 - Ayuda a víctimas - 03/2020" → "Ayuda a víctimas" */
function parseCampaignName(name: string | null): string | null {
	if (!name) return null;
	const parts = name.split(' - ');
	if (parts.length <= 1) return name.trim();
	// If the last segment looks like a date (e.g. "03/2020", "(03/2020)"), use the previous one
	const last = parts.at(-1)!.trim();
	if (/^\(?\d{1,2}\/\d{4}\)?$/.test(last) && parts.length >= 3) {
		return parts.at(-2)!.trim();
	}
	return last;
}

// ---------------------------------------------------------------------------
// Donation feed — motion layout animations
// ---------------------------------------------------------------------------

type FeedVariant = 'global' | 'global-compact' | 'emergency';

const MAX_VISIBLE_BY_VARIANT: Record<FeedVariant, number> = {
	'global': 12,
	'global-compact': 6,
	'emergency': 2,
};

const sharedSpring = {
	type: 'spring' as const,
	stiffness: 320,
	damping: 30,
};

const enterTransition = {
	layout: sharedSpring,
	y: sharedSpring,
	opacity: { delay: 0.1, duration: 0.3, ease: 'easeOut' as const },
};

function DonationFeed({ donations, variant = 'global' }: { donations: FeedDonation[]; variant?: FeedVariant }) {
	const isEmergency = variant === 'emergency';

	// Newest-first for display. The emergency feed sits in normal flow, so it only renders the visible
	// rows — otherwise faded-out rows would still take vertical space and stretch the card. The global
	// feed is absolutely positioned and keeps ONE spare row past the visible count, which is the row
	// that animates out at opacity 0; it used to keep the whole 20-item buffer, which is a metre of
	// invisible rows below the fold.
	const allItems = [...donations]
		// oxlint-disable-next-line unicorn/no-array-reverse
		.reverse()
		.slice(0, MAX_VISIBLE_BY_VARIANT[variant] + (isEmergency ? 0 : 1));

	return (
		<div className="mt-4 w-full">
			{/* Waiting state — hidden for the emergency feed to keep it compact */}
			{!isEmergency && (
				<AnimatePresence>
					{allItems.length === 0 && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.3 }}
							className="flex items-center justify-center pt-5 text-[clamp(1rem,1.3vw,1.55rem)] text-gray-300"
						>
							<span className="flex items-center gap-2">
								<span className="relative flex h-1.5 w-1.5">
									<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gray-300 opacity-75" />
									<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-gray-300" />
								</span>
								Listening for donations...
							</span>
						</motion.div>
					)}
				</AnimatePresence>
			)}

			{/* Donation rows */}
			<div className="flex flex-col items-center">
				<AnimatePresence initial={false}>
					{allItems.map((d, i) => (
						<DonationRow key={d.id} donation={d} index={i} variant={variant} />
					))}
				</AnimatePresence>
			</div>
		</div>
	);
}

function DonationRow({
	donation,
	index,
	variant = 'global',
}: {
	donation: FeedDonation;
	index: number;
	variant?: FeedVariant;
}) {
	const flagUrl = donation.country ? getCountryFlagUrl(donation.country) : undefined;
	const amount = (donation.amountCents / 100).toFixed(2);
	const campaignLabel = parseCampaignName(donation.campaignName);
	const hasLocation = flagUrl || donation.city;
	const isEmergency = variant === 'emergency';

	const maxVisible = MAX_VISIBLE_BY_VARIANT[variant];
	const targetOpacity = index >= maxVisible ? 0 : index === 0 ? 1 : Math.max(0.25, 1 - index * 0.1);

	// The emergency feed is more compact, so amounts render a touch smaller and in red.
	const amountClassName = isEmergency
		? 'shrink-0 text-[clamp(1.4rem,2.3vw,2.7rem)] font-semibold tabular-nums text-red-600'
		: 'text-primary shrink-0 text-[clamp(1.75rem,2.9vw,3.5rem)] font-semibold tabular-nums';

	return (
		<motion.div
			layout
			initial={{ opacity: 0, y: -30 }}
			animate={{ opacity: targetOpacity, y: 0 }}
			transition={enterTransition}
			className="w-full"
		>
			<div className="flex flex-col items-center justify-center px-2 py-2">
				<div className="flex flex-wrap items-center justify-center gap-2">
					<span className={amountClassName}>+{amount}€</span>
					{campaignLabel && (
						<>
							<span className="shrink-0 text-[clamp(1.35rem,2.1vw,2.5rem)] text-gray-400">para</span>
							<span className="text-[clamp(1.35rem,2.1vw,2.5rem)] font-medium text-gray-700">{campaignLabel}</span>
						</>
					)}
				</div>

				<div className="flex flex-wrap items-center justify-center gap-2">
					{donation.merchantName && (
						<span className="text-[clamp(1.35rem,2.1vw,2.5rem)] text-gray-400">
							en {donation.merchantName}
							{hasLocation && ', '}
						</span>
					)}
					{hasLocation && (
						<span className="flex items-center gap-1.5 text-[clamp(1.35rem,2.1vw,2.5rem)] text-gray-400">
							{donation.city}
							{flagUrl && (
								<img src={flagUrl} alt="" className="h-[clamp(1.1rem,1.7vw,2rem)] w-[clamp(1.1rem,1.7vw,2rem)]" />
							)}
						</span>
					)}
				</div>
			</div>
		</motion.div>
	);
}

// ---------------------------------------------------------------------------
// Mode toggle — switch between day offset and realtime
// ---------------------------------------------------------------------------

function ModeToggle({ mode, onToggle }: { mode: LiveCounterMode; onToggle: () => void }) {
	const isRealtime = mode === 'realtime';

	return (
		<div className="fixed right-4 bottom-4 flex items-center gap-2.5 rounded-full bg-white/80 px-3 py-2 shadow-md backdrop-blur-sm">
			<span className={`text-xs font-medium ${!isRealtime ? 'text-gray-900' : 'text-gray-400'}`}>-1 day</span>
			<button
				type="button"
				role="switch"
				aria-checked={isRealtime}
				aria-label={`Switch to ${isRealtime ? 'day offset' : 'realtime'} mode`}
				onClick={onToggle}
				className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
					isRealtime ? 'bg-primary' : 'bg-gray-300'
				}`}
			>
				<span
					className={`pointer-events-none inline-block h-4 w-4 translate-y-0.5 rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 ${
						isRealtime ? 'translate-x-[18px]' : 'translate-x-0.5'
					}`}
				/>
			</button>
			<span className={`text-xs font-medium ${isRealtime ? 'text-gray-900' : 'text-gray-400'}`}>Realtime</span>
		</div>
	);
}

// ---------------------------------------------------------------------------
// The mysterious fruit
// Something is growing, quietly, in the corner. No one knows quite what it is.
// Each week it takes the shape of a different fruit, and it will keep changing
// until the harvest day arrives. Hover over it, if you dare, to learn its name.
// ---------------------------------------------------------------------------

const HARVEST_DAY = new Date('2026-11-30T00:00:00').getTime();
const MOONS_UNTIL_RIPE = 40;
const ONE_MOON = 7 * 24 * 60 * 60 * 1000;

/** The shapes the mysterious fruit takes as it ripens, moon by moon. */
const FORMS_OF_THE_FRUIT = [
	{ moon: 4, emoji: '🌱', name: 'una semilla' },
	{ moon: 6, emoji: '🫛', name: 'un guisante' },
	{ moon: 7, emoji: '🫐', name: 'un arándano' },
	{ moon: 9, emoji: '🍒', name: 'una cereza' },
	{ moon: 10, emoji: '🍓', name: 'una fresa' },
	{ moon: 12, emoji: '🍋', name: 'un limón' },
	{ moon: 14, emoji: '🍑', name: 'un melocotón' },
	{ moon: 15, emoji: '🍎', name: 'una manzana' },
	{ moon: 16, emoji: '🥑', name: 'un aguacate' },
	{ moon: 17, emoji: '🍐', name: 'una pera' },
	{ moon: 18, emoji: '🫑', name: 'un pimiento' },
	{ moon: 19, emoji: '🥭', name: 'un mango' },
	{ moon: 20, emoji: '🍌', name: 'un plátano' },
	{ moon: 21, emoji: '🥕', name: 'una zanahoria' },
	{ moon: 24, emoji: '🌽', name: 'una mazorca de maíz' },
	{ moon: 26, emoji: '🥬', name: 'una lechuga' },
	{ moon: 28, emoji: '🍆', name: 'una berenjena' },
	{ moon: 30, emoji: '🥥', name: 'un coco' },
	{ moon: 33, emoji: '🍍', name: 'una piña' },
	{ moon: 35, emoji: '🍈', name: 'un melón' },
	{ moon: 37, emoji: '🥦', name: 'una coliflor' },
	{ moon: 39, emoji: '🎃', name: 'una calabaza' },
	{ moon: 40, emoji: '🍉', name: 'una sandía' },
] as const;

function divineTheFruit(now: number) {
	// When the harvest day comes, the fruit is a fruit no longer...
	if (now >= HARVEST_DAY) {
		return { moon: MOONS_UNTIL_RIPE, emoji: '👶', name: '¡ya está aquí!' };
	}
	const moon = Math.max(4, Math.round(MOONS_UNTIL_RIPE - (HARVEST_DAY - now) / ONE_MOON));
	let form: (typeof FORMS_OF_THE_FRUIT)[number] = FORMS_OF_THE_FRUIT[0];
	for (const candidate of FORMS_OF_THE_FRUIT) {
		if (moon >= candidate.moon) form = candidate;
	}
	return { moon, emoji: form.emoji, name: form.name };
}

function MysteriousFruit() {
	const { name, emoji } = divineTheFruit(Date.now());

	return (
		<div className="group fixed bottom-4 left-4 flex items-center gap-2 select-none">
			<motion.span
				className="text-[clamp(1.35rem,2.1vw,2.5rem)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
				// A brief shiver, then stillness for 30 seconds — as if it stirred.
				animate={{ rotate: [0, -10, 9, -5, 0], y: [0, -3, 0, -2, 0] }}
				transition={{
					duration: 1.2,
					times: [0, 0.2, 0.45, 0.7, 1],
					repeat: Infinity,
					repeatDelay: 30,
					ease: 'easeInOut',
				}}
			>
				{emoji}
			</motion.span>
			<span className="max-w-0 overflow-hidden text-[clamp(1.75rem,2.9vw,3.5rem)] whitespace-nowrap text-gray-400 opacity-0 transition-all duration-300 group-hover:max-w-[18rem] group-hover:opacity-100">
				{name}
			</span>
		</div>
	);
}
